/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { INSPECTOR_NODE_TYPE, isInspectorNodeDefault, TInspectorNode } from "../InspectorNodeTypes";
import {
	ISchemaResponsiveValueModel,
	isSchemaResponsiveValueModel,
	MEDIA_RESOLUTIONS_string,
	TGenericBlueprintSchema,
	TGenericModelNode
} from "@adapptio/lib-blueprint";
import { IInspectorControl_Responsive, INSPECTOR_CONTROL_TYPE, TInspectorControl } from "../controls";
import { mapModelToNodes } from "../index";
import { mapperDescriptor } from "../../modules/Core/ReferenceGuide/mappers/mapperDescriptor";
import { mapperExamples } from "../../modules/Core/ReferenceGuide/mappers/mapperExamples";
import { getParentComponentName } from "../utils";

export interface IControlResponsiveValue_EditorMetaData {
	showExpanded?: boolean;
	mainResolutionId?: MEDIA_RESOLUTIONS_string;
}

export const defaultResolutionId: MEDIA_RESOLUTIONS_string = "screenBase";

export function responsiveValueMapper(
	model: ISchemaResponsiveValueModel<TGenericBlueprintSchema>
): TInspectorNode {
	if (!isSchemaResponsiveValueModel(model)) {
		return;
	}

	const getMeta = (): IControlResponsiveValue_EditorMetaData => {
		const meta = model.schema.getEditorMetaData<IControlResponsiveValue_EditorMetaData>(model);

		return {
			showExpanded: false,
			mainResolutionId: defaultResolutionId,
			...meta
		};
	};

	const isExpanded = (): boolean => getMeta().showExpanded;

	const deps: TGenericModelNode[] = [ model, model.value, model.value.constant ];
	const controls: TInspectorControl[] = [];
	const children = [];

	const responsiveControl: IInspectorControl_Responsive = {
		key: String(model.nodeId),
		type: INSPECTOR_CONTROL_TYPE.RESPONSIVE,
		model
	};

	if (!isExpanded()) {
		const defaultModel = model.value.constant.props[getMeta().mainResolutionId];
		const constantModelNode = mapModelToNodes(defaultModel);

		if (isInspectorNodeDefault(constantModelNode)) {
			if (constantModelNode.controls.length === 0 && constantModelNode.children.length === 1) {
				const resolvedChild = constantModelNode.children[0]();
				controls.push(...resolvedChild.controls);
				deps.push(...resolvedChild.deps);
			} else {
				controls.push(...constantModelNode.controls);
			}
		}

		deps.push(...constantModelNode.deps);
	} else {
		const valueNode = mapModelToNodes(model.value);

		controls.push(...valueNode.controls);

		if (isInspectorNodeDefault(valueNode)) {
			children.push(...valueNode.children);
		}
	}

	const lastControl = controls[controls.length - 1];

	if (lastControl?.type === INSPECTOR_CONTROL_TYPE.VALUE_TYPE) {
		controls.splice(controls.length - 1, isExpanded() ? 1 : 0, responsiveControl);
	} else {
		controls.push(responsiveControl);
	}

	return {
		type: INSPECTOR_NODE_TYPE.DEFAULT,
		key: `${model.nodeId}`,
		label: model.schema.opts.label,
		tooltip: model.schema.opts.description,
		required: model.schema.opts.constraints?.required || false,
		default: model.schema.opts.default || null,
		options: model.schema.opts["options"],
		referenceData: getParentComponentName(model),
		descriptor: mapperDescriptor(model.schema),
		predefineValues: model.schema.opts.value.opts?.["constants"]
			? model.schema.opts.value.opts?.["constants"].map((el) => el.value)
			: null,
		examples: mapperExamples(model.schema),
		deps: [ ...deps ],
		controls: [ ...controls ],
		children: children
	};
}
